export interface RoutePoint {
  readonly x: number;
  readonly y: number;
}

const DEFAULT_CORNER_RADIUS = 12;

const distance = (from: RoutePoint, to: RoutePoint): number =>
  Math.hypot(to.x - from.x, to.y - from.y);

const formatPoint = (point: RoutePoint): string => [point.x, point.y].join(' ');

const pointTowards = (
  from: RoutePoint,
  to: RoutePoint,
  distanceFromFrom: number,
): RoutePoint => {
  const total = distance(from, to);

  if (total === 0) {
    return from;
  }

  const ratio = distanceFromFrom / total;

  return {
    x: from.x + (to.x - from.x) * ratio,
    y: from.y + (to.y - from.y) * ratio,
  };
};

export const pointsToRoundedPath = (
  points: readonly RoutePoint[],
  radius = DEFAULT_CORNER_RADIUS,
): string => {
  const [first, ...rest] = points;

  if (!first) {
    return '';
  }

  const segments = [`M ${formatPoint(first)}`];
  let previous = first;

  rest.forEach((current, index) => {
    const next = rest[index + 1];

    if (!next) {
      segments.push(`L ${formatPoint(current)}`);
      return;
    }

    const radiusIn = Math.min(radius, distance(previous, current) / 2);
    const radiusOut = Math.min(radius, distance(current, next) / 2);
    const cornerStart = pointTowards(current, previous, radiusIn);
    const cornerEnd = pointTowards(current, next, radiusOut);

    segments.push(`L ${formatPoint(cornerStart)}`);
    segments.push(`Q ${formatPoint(current)} ${formatPoint(cornerEnd)}`);
    previous = current;
  });

  return segments.join(' ');
};
